import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Link2 } from 'lucide-react';

import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const ResourceLinkRouter = () => {
  const { shortCode } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, error
  const [error, setError] = useState('');

  useEffect(() => {
    if (!shortCode) {
      setStatus('error');
      setError('Invalid resource link.');
      return;
    }

    resolveResourceLink();
  }, [shortCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolveResourceLink = async () => {
    try {
      // Step 1: Fetch public metadata to validate the link exists
      const metaResponse = await fetch(`${API_BASE}/api/public/resource-links/${shortCode}`);

      if (!metaResponse.ok) {
        if (metaResponse.status === 404) {
          setStatus('error');
          setError('This resource link is invalid or has expired.');
          return;
        }
        setStatus('error');
        setError('Something went wrong. Please try again.');
        return;
      }

      // Step 2: Check authentication
      const token = localStorage.getItem('memberToken') || localStorage.getItem('customerToken');

      if (!token) {
        // Not authenticated - redirect to login with return URL
        navigate(`/login?redirect=/r/${shortCode}`, { replace: true });
        return;
      }

      // Step 3: Authenticated - call navigate endpoint for deep link info
      const navResponse = await fetch(`${API_BASE}/api/customer/resource-links/${shortCode}/navigate`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!navResponse.ok) {
        if (navResponse.status === 401) {
          // Token expired - redirect to login
          navigate(`/login?redirect=/r/${shortCode}`, { replace: true });
          return;
        }
        setStatus('error');
        setError('Unable to access this resource. Please try again.');
        return;
      }

      const navData = await navResponse.json();

      // Step 4: Navigate to dashboard with deep link state
      navigate('/', {
        replace: true,
        state: {
          deepLink: {
            tab: navData.tab,
            section: navData.section,
            itemId: navData.itemId
          }
        }
      });
    } catch (err) {
      setStatus('error');
      setError('Something went wrong. Please try again.');
    }
  };

  if (status === 'error') {
    return (
      <div className="member-login-bg relative flex items-center justify-center px-4">
        <div className="login-orb login-orb-1"></div>
        <div className="login-orb login-orb-2"></div>
        <div className="login-orb login-orb-3"></div>

        <Card className="member-login-card w-full max-w-md relative z-10 dojo-animate">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-2xl font-outfit font-bold text-stone-800 dark:text-stone-100 mb-2">
              Link Not Found
            </h2>
            <p className="text-stone-600 dark:text-stone-400 mb-6 font-dm-sans">
              {error}
            </p>
            <div className="space-y-3">
              <Button
                variant="memberPrimary"
                className="w-full"
                onClick={() => navigate('/')}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="memberSecondary"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Sign In
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  return (
    <div className="member-login-bg relative flex items-center justify-center px-4">
      <div className="login-orb login-orb-1"></div>
      <div className="login-orb login-orb-2"></div>
      <div className="login-orb login-orb-3"></div>

      <Card className="member-login-card w-full max-w-md relative z-10 dojo-animate">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Link2 className="h-8 w-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-xl font-outfit font-bold text-stone-800 dark:text-stone-100 mb-3">
            Opening Resource
          </h2>
          <Loader2 className="h-6 w-6 animate-spin text-amber-600 mx-auto" />
        </CardContent>
      </Card>
    </div>
  );
};

export default ResourceLinkRouter;
